
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface UsePageLoadingOptions {
  minDuration?: number;
  timeout?: number;
}

export const usePageLoading = (options: UsePageLoadingOptions = {}) => {
  const { minDuration = 500, timeout = 5000 } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const location = useLocation();

  // Função para iniciar o loading
  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  // Função para parar o loading
  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Efeito para detectar mudanças de rota
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
      return;
    }

    // Iniciar loading quando a rota mudar
    startLoading();

    // Timeout de segurança para evitar loading infinito
    const timeoutId = setTimeout(() => {
      console.warn('Loading timeout reached, forcing stop');
      stopLoading();
    }, timeout);

    // Simular carregamento da página (em uma aplicação real, isso seria baseado em eventos reais)
    const loadingTimer = setTimeout(() => {
      stopLoading();
    }, Math.random() * 1000 + 300); // Simula entre 300ms e 1.3s

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(loadingTimer);
    };
  }, [location.pathname, startLoading, stopLoading, timeout, isInitialLoad]);

  // Efeito para detectar quando a página está realmente carregada
  useEffect(() => {
    const handlePageLoad = () => {
      stopLoading();
    };

    // Se a página já carregou
    if (document.readyState === 'complete') {
      stopLoading();
    }

    window.addEventListener('load', handlePageLoad);
    
    return () => {
      window.removeEventListener('load', handlePageLoad);
    };
  }, [stopLoading]);

  return {
    isLoading,
    startLoading,
    stopLoading,
    minDuration
  };
};
